import { motion } from "framer-motion";
import { Wallet, BarChart3, Receipt, Phone, ArrowRightLeft, Globe2, Gift, MapPin, Clock3, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PageHero } from "../components/pt/PageHero";
import { ClosingCTA } from "../components/pt/ClosingCTA";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const SERVICE_ICONS = [Wallet, BarChart3, Receipt, Phone, ArrowRightLeft, Globe2];
const PERK_ICONS = [Gift, MapPin, Clock3, ShieldCheck];

const People = () => {
    const { t } = useLang();
    const p = t.peoplePage;
    useDocumentTitle("/personas");
    // piece 3 marker: top-left on
    const piecePattern = [
        ["on", ""],
        ["", ""],
        ["", "gap"],
    ];

    return (
        <div data-testid="people-page">
            <PageHero
                audienceTag={p.audienceTag}
                title={p.title}
                lead={p.lead}
                piecePattern={piecePattern}
                testId="people-hero"
            />

            {/* Services available */}
            <section className="sec" data-testid="people-services">
                <div className="wrap">
                    <h2 className="h2 mb-12 max-w-[22ch]">
                        {p.services.title}
                    </h2>
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {p.services.items.map((s, i) => {
                            const Icon = SERVICE_ICONS[i] || Wallet;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                                    className="group rounded-xl border border-linea bg-white p-6 transition-all hover:-translate-y-1 hover:border-tinta/40 hover:shadow-[0_30px_60px_-32px_rgba(20,35,64,0.3)]"
                                    data-testid={`people-service-${i}`}
                                >
                                    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg bg-tinta text-white transition-colors group-hover:bg-coral-profundo">
                                        <Icon size={20} />
                                    </div>
                                    <h3 className="h3 mb-2">{s.h}</h3>
                                    <p className="text-[0.98rem] text-acero">
                                        {s.p}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Perks */}
            <section className="sec mist" data-testid="people-perks">
                <div className="wrap">
                    <h2 className="h2 mb-12 max-w-[22ch]">
                        {p.perks.title}
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2">
                        {p.perks.items.map((perk, i) => {
                            const Icon = PERK_ICONS[i] || Gift;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: i % 2 === 0 ? -24 : 24 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.55, delay: i * 0.08 }}
                                    className="flex items-start gap-5 rounded-2xl bg-white p-7 shadow-[0_1px_0_var(--pt-linea)]"
                                    data-testid={`people-perk-${i}`}
                                >
                                    <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-coral-profundo text-white">
                                        <Icon size={22} />
                                    </div>
                                    <div>
                                        <h3
                                            className="h3 mb-2"
                                            style={{ fontSize: "1.3rem" }}
                                        >
                                            {perk.h}
                                        </h3>
                                        <p className="text-[1rem] text-acero">
                                            {perk.p}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <ClosingCTA testId="people-closing" />
        </div>
    );
};

export default People;
